import { BAOCustomElement } from '@bao/custom-element'
import { debounce, getOffsetTop } from '@bao/utils'
import { MediaBreakpoints } from '@bao/media'

const PARALLAX_STRENGTH = 0.5

class ParallaxImage extends BAOCustomElement() {
  connectedCallback () {
    if (Shopify.designMode) return
    if (this.prefersReducedMotion) return
    if (!this.els.item?.exists || !this.els.container?.exists) return

    super.connectedCallback()

    this.isTicking = false
    this.lastKnownY = window.scrollY

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)

    this.requestTick()
  }

  setupListeners () {
    super.setupListeners()

    this.onScroll = this.onScroll.bind(this)
    this.onResize = this.onResize.bind(this)

    this.debouncedOnResize = debounce(this.onResize, 200)

    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.debouncedOnResize)
  }

  onScroll () {
    this.lastKnownY = window.scrollY

    this.requestTick()
  }

  onResize () {
    this.lastKnownY = window.scrollY

    this.requestTick()
  }

  requestTick () {
    if (this.isTicking) return

    this.isTicking = true

    window.requestAnimationFrame(this.updateEl)
  }

  updateEl () {
    this.isTicking = false

    if (this.prefersReducedMotion) return
    if (!this.els.item?.element || !this.els.container?.element) return

    this.moveParallaxItem()
  }

  moveParallaxItem () {
    if (!this.offsetHeight) return

    const progress = this.isMobile
      ? this.mobileScrollProgress
      : this.scrollProgress

    const transformAmountY = this.getTransformY(progress)

    this.setTransform(transformAmountY)
  }

  getTransformY (progress) {
    if (this.isMobile) {
      const topY = (this.availableDistance * this.directionModifier) / -2
      const bottomY =
        ((this.availableDistance + this.mobileOverflow) *
          this.directionModifier) /
        2

      const reducedTopY = bottomY - (bottomY - topY) * PARALLAX_STRENGTH

      return reducedTopY + (bottomY - reducedTopY) * progress
    }

    const amountToMoveY =
      this.availableDistance * this.directionModifier * PARALLAX_STRENGTH

    return amountToMoveY * progress - amountToMoveY / 2
  }

  setTransform (y) {
    this.els.item.element.style.setProperty('--ParallaxY', `${y}px`)
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.removeEventListener('scroll', this.onScroll)

    if (this.debouncedOnResize) {
      window.removeEventListener('resize', this.debouncedOnResize)
    }

    this.isTicking = false

    this.els.item?.element?.style.removeProperty('--ParallaxY')
  }

  get scrollPositions () {
    const offsetTop = getOffsetTop(this)

    return {
      start: offsetTop - window.innerHeight,
      end: offsetTop + this.offsetHeight,
      center: offsetTop + this.offsetHeight / 2 - window.innerHeight / 2,
    }
  }

  get scrollProgress () {
    const { start, end } = this.scrollPositions

    if (this.lastKnownY <= start) return 0
    if (this.lastKnownY >= end) return 1

    return (this.lastKnownY - start) / (end - start)
  }

  get mobileScrollProgress () {
    const { start, center } = this.scrollPositions

    if (this.lastKnownY <= start) return 0
    if (this.lastKnownY >= center) return 1

    return (this.lastKnownY - start) / (center - start)
  }

  get availableDistance () {
    const distance =
      this.els.container.element.offsetHeight -
      this.els.item.element.offsetHeight

    return Math.max(0, distance)
  }

  get directionModifier () {
    return this.reverseDirection ? -1 : 1
  }

  get mobileOverflow () {
    const overflow = Number(this.dataset.mobileOverflow)

    return Number.isNaN(overflow) ? 0 : overflow
  }

  get reverseDirection () {
    return this.dataset.reverseDirection === 'true'
  }

  get prefersReducedMotion () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  get isMobile () {
    return MediaBreakpoints.isXsViewport
  }
}

if (!customElements.get('parallax-image')) {
  customElements.define('parallax-image', ParallaxImage, {
    extends: 'section',
  })
}
